/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */
define([
    '../appContext',
    '../services/services',
    './../utils/tools',
    './localStorage',
    '../utils/dvtypes'],
function (app, services, tools, localStorage, types) {
    "use strict";

    var s = {};

    //#region Properties

    s.currentUser = {};
    s.memberOf = [];
    s.canConfigureAny = false;
    s.variables = new tools.dictionary();
    s.config = null;
    s.sessionGUID = null;
    s.serverTZOffset = 0;

    // Wrap these properties in a context object so that we can store / retrieve easily from indexed db...AL
    s.appContext = null;

    Object.defineProperties(s, {
        'isDesigner': {
            get: function () {
                return s._isDesigner && app.designer != null;
            }
        }
    });

    //#endregion

    /**
     * Clears the variables dictionary and persists certain variables if needed
     * @param {Boolean} [keepLocalVariables = false] - Flag to determine if we should keep the "dvVar" local variables
     */
    s.clearVariables = function (keepLocalVariables = false) {
        if (keepLocalVariables) {
            s.variables.forEach((val, key) => {
                if (!key.startsWith('{dvVar_')) {
                    s.variables.remove(key);
                }
            });
        }
        else {
            s.variables.clear();
        }
    };

    /**
     * Update app context and config information from the current session as the user is logging out.
     * @param {function} [callback = tools.noop]
     * @param {boolean} [flagPersist = true] - TRUE, persist the updated app context from logout; FALSE, do not persis app context from this logout (switch user?)
     * @param {boolean} [keepLocalVariables = false] - Flag to determine if we should keep the "dvVar" local variables
     */
    s.logUserOut = function (callback = tools.noop, flagPersist = true, keepLocalVariables = false) {
        // Remove any user variables
        s.clearVariables(keepLocalVariables);

        s.currentUser = {};
        s.memberOf = [];
        s.canConfigureAny = false;
        s.config = null;
        s.sessionGUID = null;
        if (s.appContext.user) {
            s.appContext.user.Definition.clientProperties.stayLoggedIn = false;
            s.appContext.user.Definition.clientProperties.stayLoggedInSSOType = null;
        }
        s.appContext.lastServerUpdate = null;
        if (flagPersist) {
            s.persistAppContext(function () {
                callback();
            });
        }
        else {
            callback();
        }
    };

    // This will perform a check for permissions based off of the permission type, record id, and the access level requested...AL
    s.checkPermission = function (tableName, recordID, accessLevel) {
        var sgMatch = false;

        switch (tableName) {
            case "RecordType":
            case "ValueStatus":
            case "Category":
                for (var seci = app.dv.cache.securities.allSecurities.Values.length - 1; seci >=0; seci--) {
                    var sec = app.dv.cache.securities.allSecurities.Values[seci];
                    if (sec.TblName == tableName &&
                        sec.RecordID == recordID &&
                        sec.SecurityLevel == accessLevel) {
                        sgMatch = true;
                        break;
                    }
                }
                break;
            default:
                break;
        }

        if (sgMatch == false) {
            // No security is assigned, allow access...AL
            return true;
        } else { // Otherwise - evaluate the security membership...AL
            for (var seci = app.dv.cache.securities.allSecurities.Values.length - 1; seci >= 0; seci--) {
                var sec = app.dv.cache.securities.allSecurities.Values[seci];


                for (var sgmi = s.memberOf.length - 1; sgmi >= 0; sgmi--) {
                    var sg = s.memberOf[sgmi];

                    if (sec.SecurityGroupUID == sg.UID &&
                        sec.SecurityLevel == accessLevel &&
                        sec.RecordID == recordID &&
                        sec.TblName == tableName) {
                        // Found a match - user has permission...AL
                        return true;
                    }
                }
            }

            // If we've gotten here, then the user does not have permission...AL
            return false;
        }
    };

    // This will assign write privilages to all individual properties based off of the current user...AL
    s.secureProperties = function () {
        let slength = app.dv.cache.recordTypes.allRecordTypes.Values.length;
        for (let rti = 0; rti < slength; rti++) {
            let rt = app.dv.cache.recordTypes.allRecordTypes.Values[rti];

            if (s.checkPermission("RecordType", rt.RecordTypeID, app.dv.types.SecurityLevels.Write) == true
                && rt.clientProperties.childCategories != null) {
                for (let ci = 0, slength = rt.clientProperties.childCategories.length; ci < slength; ci++) {
                    let c = rt.clientProperties.childCategories[ci];
                    for (let pi in c.clientProperties.childProperties) {
                        let p = c.clientProperties.childProperties[pi];
                        p.clientProperties.canUserWrite = true;
                    }
                }
            } else if (rt.clientProperties.childCategories != null ) {
                for (let ci = 0, slength = rt.clientProperties.childCategories.length; ci < slength; ci++) {
                    let c = rt.clientProperties.childCategories[ci];
                    if (s.checkPermission("Category", c.CategoryID, app.dv.types.SecurityLevels.Write) == true) {
                        for (let pi in c.clientProperties.childProperties) {
                            let p = c.clientProperties.childProperties[pi];
                            p.clientProperties.canUserWrite = true;
                        }
                    }
                }
            }
        }
    };

    /**
     * Return if the current user is a member of a specified security group.
     * @param {number|string|object} SecurityGroupID - The SecurityGroupID, UID, or linker object of the security group to check for
     * @returns {boolean} True if the current user is a member of the specified group, false otherwise
     */
    s.isMemberOf = function (SecurityGroupID) {
        return s.memberOf.any(
            function (sg) {
                return (typeof SecurityGroupID === 'object' && SecurityGroupID != null) ?
                    sg.UID === SecurityGroupID.UID :
                    (sg.SecurityGroupID == SecurityGroupID || sg.UID === SecurityGroupID);
                //NOTE: the "==" is intentional so the user does not have to know the type of SecurityGroupID (isMemberOf(1) = isMemberOf("1"))
            });
    };

    //#region Connection handlers

    /**
     * Object containing references to tasks to run on specific connection-related events
     * @type {Object}
     */
    s.connectionHandlers = {
        restored: new Map(),
        lost: new Map(),
        versionChanged: new Map()
    };

    /**
     * Registers the form's task to the specific connection event
     * @param {Number} handlerType - The event type (types.ConnectionHandlerTypes) to listen to
     * @param {String} mvcRef - The mvc instance to run the task on
     * @param {Number} taskID - The form's task to run when the event occurs
     */
    s.registerConnectionHandler = function (handlerType, mvcRef, taskID) {
        switch (handlerType) {
            case types.ConnectionHandlerTypes.restored:
                s.connectionHandlers.restored.set(mvcRef, taskID);
                break;
            case types.ConnectionHandlerTypes.lost:
                s.connectionHandlers.lost.set(mvcRef, taskID);
                break;
            case types.ConnectionHandlerTypes.versionChanged:
                s.connectionHandlers.versionChanged.set(mvcRef, taskID);
                break;
            default:
                console.warn(`Unknown connection handler type ${handlerType} when attempting to register task`);
        }
    };

    /**
     * Unregisters any connection handlers for the given mvc
     * @param {Object} mvc - the MVC to unregister
     */
    s.unregisterConnectionHandlers = function (mvc) {
        s.connectionHandlers.restored.delete(mvc);
        s.connectionHandlers.lost.delete(mvc);
        s.connectionHandlers.versionChanged.delete(mvc);
    };

    /**
     * Handles the client when the session has restored connection to the service.
     * @returns {Promise<void>} Promise that resolves when all macros have been executed
     */
    s.handleConnectionRestored = async function () {
        s.hasLostConnection = false;
        for (let [mvc, taskID] of s.connectionHandlers.restored) {
            await app.dv.mvc.executeMacro(mvc, taskID);
        }
        app.dv.isNoServiceConnection(false);
    };

    /**
     * Handles the client when the session has lost connection to the service.
     * If any "lost connection" handlers are defined and available to run, the default popup will be ignored.
     * @returns {Promise<void>} Promise that resolves when all macros have been executed
     */
    s.handleConnectionLost = async function () {
        s.hasLostConnection = true;
        let handled = false;
        for (let [mvc, taskID] of s.connectionHandlers.lost) {
            if (mvc.drivingView != null &&  mvc.drivingView.Definition.ViewMacros.find(m => m.MacroID === taskID)) {
                handled = true;
                await app.dv.mvc.executeMacro(mvc, taskID);
            }
        }
        if (!handled) {
            app.dv.isNoServiceConnection(true);
        }
    };

    /**
     * Handles the client when we have detected a version mismatch from the service.
     * If any "version changed" handlers are defined and available to run, the default popup will be ignored.
     * @param {string} serviceVersion - The differing service version
     * @returns {Promise<void>} Promise that resolves when all macros have been executed
     */
    s.handleVersionMismatch = async function (serviceVersion) {
        let handled = false;
        // Store a reference to the updated version to the session object so that it can be used inside the tasks' expressions.
        s.updatedServiceVersion = serviceVersion;
        api.build.updatedServiceVersion = serviceVersion;
        for (let [mvc, taskID] of s.connectionHandlers.versionChanged) {
            if (mvc.drivingView != null &&  mvc.drivingView.Definition.ViewMacros.find(m => m.MacroID === taskID)) {
                handled = true;
                await app.dv.mvc.executeMacro(mvc, taskID);
            }
        }
        if (!handled) {
            app.dv.mismatchedVersion(serviceVersion);
        }
    };

    //#endregion

    /*
     * This function will check the security settings on the specified action chain with
     * the current user's group membership.
     * Returns true if the user can execute the actionChain specified, otherwise false.
     */
    s.canExecuteActionChain = function (actionChain) {
        if (actionChain.CanExecuteGroups != null && actionChain.CanExecuteGroups.length > 0) {
            for (var k = actionChain.CanExecuteGroups.length - 1; k >= 0; k--) {
                // pass the security ID to see if the current user is a member able to use the button.
                if (s.isMemberOf(actionChain.CanExecuteGroups[k]) == true) {
                    return true;
                }
            }
            return false;       //If we drop out of the loop, the user is not in any group assigned...EK
        }
        else {
            // if there are no groups tied to this button, then anyone can use it.
            return true;
        }
    };

    //#region Service Calls

    /**
     * Initializes the designer caches with the available entities
     * @param {object} r - The results from the service call to get the entity cache
     * @param {boolean} rememberMeFlag
     * @param {boolean} cacheCredentialsFlag
     * @param {string} SSOType - The current SSO type
     * @param {boolean} flagSwitch
     * @param {function} callback
     * @param {boolean} [keepLocalVariables = false] - Flag to determine if we should keep the "dvVar" local variables
     * @returns {Promise}
     */
    function loadDesignerCache (r, rememberMeFlag, cacheCredentialsFlag, SSOType, flagSwitch, callback, keepLocalVariables = false) {
        let CA = app.dv.cache;

        //remove unused system setting properties...TD
        r.EntityCache.Config.ConfigValue.macroLibrary_RT_Settings = undefined;
        r.EntityCache.Config.ConfigValue.crashReportSettings = undefined;
        r.EntityCache.Config.ConfigValue.diagramNodeRecordTypeSettings = undefined;
        r.EntityCache.Config.ConfigValue.iconSettings = undefined;
        r.EntityCache.Config.ConfigValue.tuneOptionSettings.ForceCommentOnSave = undefined;

        //set session config to result
        s.configEntity = r.EntityCache.Config;
        s.config = s.configEntity.ConfigValue;
        s.appContext.config = s.config;
        if (!flagSwitch) {
            s.persistAppContext();
        }

        //This user is a designer, we need to put all the cache items where they belong and organize it...EK
        CA.recordTypes.mergeCache(r.EntityCache.RecordTypes);

        CA.categories.mergeCache(r.EntityCache.Categories);

        CA.properties.mergeCache(r.EntityCache.Properties);

        CA.loadValueStatusCache(r.EntityCache.ValueStatuses);

        CA.loadViewCache(r.EntityCache.Views);

        CA.loadSecurityCache(r.EntityCache.Security);

        CA.loadSecurityGroupCache(r.EntityCache.SecurityGroups);

        CA.loadSecurityGroupLookupCache(r.EntityCache.SecurityGroupLookups);

        CA.loadPackageCache(r.EntityCache.Packages);

        CA.loadStyleCache(r.EntityCache.Styles);

        CA.loadTagCache(r.EntityCache.Tags);

        CA.loadTagLinkCache(r.EntityCache.TagLinks);

        CA.loadExtensionCache(r.EntityCache.Extensions, true);

        CA.loadPendingChangeCache(r.EntityCache.PendingChanges);

        //Organize the entity cache now that we have fully loaded all entities (NOTE: if a non designer logs in, this organization happens during the lazy loading of configuration - no tags)...EK
        CA.organizeEntityCache();

        let thisUserPendingChanges = CA.pendingChanges.getPendingChangesForThisUser().select(change => mergePendingChange(change));

        return Promise.all(thisUserPendingChanges).then(() => {
            //Now, apply any pending view changes this designer may have in their local indexed db...EK
            if (localStorage.dataStores.PendingViewChanges != null) {
                //We have the Pending Changes stored, see if there are changes and apply them to the view Cache...EK
                return localStorage.getAllRecords(localStorage.dataStores.PendingViewChanges).then((results) => {
                    let pendingChangePromises = [];
                    //Overwrite the global cache with pending changes...EK
                    results.forEach((localStoreViewChange) => {
                        let handlePendingViewChange = (localAlteredView) => {
                            // is this pending change already in our pending changes cache?
                            let serviceCachedPendingChange = CA.pendingChanges.getPendingChangesForThisUser().where(change => change.EntityID === localAlteredView.ViewID).first(),
                                savingPendingChange;
                            if (serviceCachedPendingChange != null) {
                                // we have a pending View change in local storage and a PendingChange in cache for the same view.

                                // do the pending changes match?
                                if (app.designer.utils.isEntityDirty(localAlteredView.Definition, serviceCachedPendingChange.Definition)
                                    || localAlteredView.Name !== serviceCachedPendingChange.EntityName) {

                                    let priorState = localAlteredView._State;

                                    // before we can update the definition, we need to add client properties to this locally stored change..WR
                                    app.dv.cache.views.applyClientProperties(localAlteredView);

                                    // update the pending change object to the new entity values; note this will update it's State.
                                    app.dv.entities.PendingChange.updateDefinition(serviceCachedPendingChange, localAlteredView);

                                    switch (priorState) {
                                        case app.dv.types.EntityStates.Deleted:
                                            // pending change should be deleted.
                                            serviceCachedPendingChange._State = priorState;
                                            break;

                                        default:
                                            localAlteredView._State = app.dv.types.EntityStates.Modified;
                                            break;
                                    }

                                    // save (or delete) the pending change from the service entities.
                                    return app.designer.saveEntityToServer(serviceCachedPendingChange, "PendingChangeID", app.dv.services.endpoints.savePendingChanges, CA.pendingChanges.allPendingChanges).then((result) => {
                                        if (result) {
                                            if (priorState !== app.dv.types.Deleted) {
                                                // get the saved entity.
                                                serviceCachedPendingChange = CA.pendingChanges.allPendingChanges.getVal(tools.createEntityLinker(app.dv.types.EntityTypes.PendingChange, serviceCachedPendingChange));

                                                return mergePendingChange(serviceCachedPendingChange);
                                            }
                                        }
                                        else {
                                            app.browser.notifier.showToast(`Unable to save pending change for entity type ${serviceCachedPendingChange.TableName}, ID ${serviceCachedPendingChange.EntityID}`, 10000);
                                            return Promise.reject(new Error(`Unable to save pending change for entity type ${serviceCachedPendingChange.TableName}, ID ${serviceCachedPendingChange.EntityID}`));
                                        }
                                    }).then(() => {
                                        // remove the pending View change from the local storage.
                                        return localStorage.deleteRecord(localStorage.dataStores.PendingViewChanges, serviceCachedPendingChange.EntityID);
                                    });
                                }
                                else {
                                    // the pending View change and the cached PendingChange match - simply remove the pending View change from local storage.
                                    return localStorage.deleteRecord(localStorage.dataStores.PendingViewChanges, serviceCachedPendingChange.EntityID);
                                }
                            }
                            else {
                                let priorState = localAlteredView._State;

                                // does the view currently exist anymore?
                                let cachedRef = CA.views.allViews.getVal(localAlteredView.ViewID);
                                if (cachedRef == null || priorState === app.dv.types.EntityStates.Deleted) {
                                    // if there is no view, we need to delete the pending view from local storage..WR
                                    // remove the pending View change from the local storage - that's all we have to do since the view should have been removed..WR
                                    return localStorage.deleteRecord(localStorage.dataStores.PendingViewChanges, localAlteredView.ViewID);
                                }

                                localAlteredView._State = app.dv.types.EntityStates.Modified;

                                // add a temp clientProperties so that we can create a new PendingChange object..WR
                                localAlteredView.clientProperties = {
                                    entityType: app.dv.types.EntityTypes.View
                                };
                                // there is no server cached PendingChange for this local storage pending View change; we need to have one.
                                serviceCachedPendingChange = app.designer.factory.newPendingChange(localAlteredView);
                                CA.pendingChanges.allPendingChanges.add(serviceCachedPendingChange.PendingChangeID, serviceCachedPendingChange);

                                // save (or delete) the pending change from the service entities.
                                return app.designer.saveEntityToServer(serviceCachedPendingChange, "PendingChangeID", app.dv.services.endpoints.savePendingChanges, CA.pendingChanges.allPendingChanges).then((result) => {
                                    if (result) {
                                        if (priorState !== app.dv.types.Deleted) {
                                            // add the new PendingChange object to the cache.
                                            serviceCachedPendingChange = CA.pendingChanges.allPendingChanges.getVal(tools.createEntityLinker("PendingChange", serviceCachedPendingChange));

                                            return mergePendingChange(serviceCachedPendingChange);
                                        }
                                    }
                                    else {
                                        app.browser.notifier.showToast(`Unable to save pending change for entity type ${serviceCachedPendingChange.TableName}, ID ${serviceCachedPendingChange.EntityID}`, 10000);
                                        return Promise.reject(new Error(`Unable to save pending change for entity type ${serviceCachedPendingChange.TableName}, ID ${serviceCachedPendingChange.EntityID}`));
                                    }
                                }).then(() => {
                                    // remove the pending View change from the local storage.
                                    return localStorage.deleteRecord(localStorage.dataStores.PendingViewChanges, serviceCachedPendingChange.EntityID);
                                });
                            }
                        };

                        pendingChangePromises.push(handlePendingViewChange(localStoreViewChange));
                    });

                    // process all the promises to deal with reconciling any local storage changes with the pending changes cache.
                    return Promise.all(pendingChangePromises);
                }).then(() => {
                    // Populate the extensions
                    if (flagSwitch) {
                        // we do not want to persist and cause this switched user to be the default logged in user.
                        s.getUserInfoValues(() => {
                            callback(true);
                        }, keepLocalVariables);
                    }
                    else {
                        s.finishLogin(rememberMeFlag, cacheCredentialsFlag, callback, SSOType, keepLocalVariables);
                    }
                }).catch((err) => {
                    // log the error, but allow the user to login.
                    console.error(err.message);
                    console.error("One or more pending changes were not restored; your local and remote pending changes may not be reflected in this session. Restart if you want to attempt to recover.");

                    // Populate the extensions
                    if (flagSwitch) {
                        // we do not want to persist and cause this switched user to be the default logged in user.
                        s.getUserInfoValues(() => {
                            app.dv.actionLibrary.executeShowMessage("One or more pending changes were not restored; your local and remote pending changes may not be reflected in this session. Restart if you want to attempt to recover.");
                            callback(true);
                        }, keepLocalVariables);
                    }
                    else {
                        //
                        s.finishLogin(rememberMeFlag, cacheCredentialsFlag, callback, SSOType, keepLocalVariables);
                        app.dv.actionLibrary.executeShowMessage("One or more pending changes were not restored; your local and remote pending changes may not be reflected in this session. Restart if you want to attempt to recover.");
                    }
                });
            }
            else {
                //Else: We don't have a pending changes store, just ignore the logic above...EK    }

                // Populate the extensions
                if (flagSwitch) {
                    // we do not want to persist and cause this switched user to be the default logged in user.
                    s.getUserInfoValues(() => {
                        callback(true);
                    }, keepLocalVariables);
                }
                else {
                    //
                    s.finishLogin(rememberMeFlag, cacheCredentialsFlag, callback, SSOType, keepLocalVariables);
                }
            }
        });
    }

    /**
     * Merge the specified pending change into the originating entity cache (typically allViews & allOriginalViews).
     * @param {PendingChange} pendingChangeEntity
     * @returns {Promise}
     */
    function mergePendingChange(pendingChangeEntity) {
        let CA = app.dv.cache;

        if (pendingChangeEntity == null) {
            console.warn("Unable to merge null pending change entity.");
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            let clonedView, viewRef = CA.views.allOriginalViews.getVal(pendingChangeEntity.EntityID);
            if (viewRef != null) {
                clonedView = tools.deepClone(viewRef, "clientProperties");
                //Ensure the client properties are applied to the pending changes view too...EK
                clonedView.clientProperties = viewRef.clientProperties;
                clonedView.Definition = tools.deepClone(pendingChangeEntity.Definition);
                clonedView._State = app.dv.types.EntityStates.Unmodified;

                // this has already been organized, just need to update the normal view cache with the pending change; the original view is still the original.
                CA.views.allViews.add(clonedView.ViewID, clonedView);
                resolve();
            }
            else {
                // not already organized, so need to get the current cached view for replacing with the pending change.
                viewRef = CA.views.allViews.getVal(pendingChangeEntity.EntityID);
                if (viewRef != null) {
                    clonedView = tools.deepClone(viewRef, "clientProperties");
                    //Ensure the client properties are applied to the pending changes view too...EK
                    clonedView.clientProperties = viewRef.clientProperties;
                    clonedView.Definition = tools.deepClone(pendingChangeEntity.Definition);
                    clonedView._State = app.dv.types.EntityStates.Unmodified;

                    //Track this view in case there is an undo on this view...EK
                    CA.views.allOriginalViews.add(viewRef.ViewID, viewRef);

                    // replace this cache object with the pending changes one...EK
                    CA.views.allViews.add(clonedView.ViewID, clonedView);
                    resolve();
                }
                else {
                    // This view has been deleted, delete the pending change.
                    pendingChangeEntity._State = app.dv.types.EntityStates.Deleted;
                    // save (or delete) the pending change from the service entities.
                    app.designer.saveEntityToServer(pendingChangeEntity, "PendingChangeID", app.dv.services.endpoints.savePendingChanges, CA.pendingChanges.allPendingChanges).then((result) => {
                        if (result) {
                            resolve();
                        }
                        else {
                            reject(new Error(`Unable to delete pending change for entity type ${pendingChangeEntity.TableName}, ID ${pendingChangeEntity.EntityID} from service.`));
                        }
                    });
                }
            }
        });
    }

    /**
     * Set the authenticated user context to the one specified by the service response.
     * @param {object} r - User response context to set for an authenticated user.
     * @param {boolean} rememberMeFlag - TRUE, remember login name; FALSE, do not remember login name
     * @param {boolean} cacheCredentialsFlag - TRUE, cache credentials; FALSE, do not cache credentials
     * @param {String} SSOType - optional SSO auth type being used
     * @param {boolean} flagSwitch - TRUE, setting context through Switch User; FALSE, not setting context through Switch User
     * @param {function} [callback = tools.noop] - The function to call back with result of the user authentication.
     * @param {boolean} [keepLocalVariables = false] - Flag to determine if we should keep the "dvVar" local variables
     */
    s.setUserContext = function(r, rememberMeFlag, cacheCredentialsFlag, SSOType, flagSwitch, callback = tools.noop, keepLocalVariables = false) {
        let CA = app.dv.cache;

        // User is authenticated, persist currentUser and Session GUID & update the server session with the returned information.
        app.connection.serverSession.User = s.currentUser.User = r.User;
        s.currentUser.User.Definition = tools.entityParse(s.currentUser.User.Definition);
        app.connection.serverSession.sessionGUID = s.sessionGUID = r.SessionGUID;
        app.connection.serverSession.MemberOf = s.memberOf = r.MemberOf;
        app.connection.serverSession.isDesigner = s._isDesigner = !!r.IsDesigner;

        /**
         * Need to update the user (and possibly designer) workspace after user variables are loaded.
         */
        let updateWorkspaces = (result) => {
            // if this is the first login, there won't be a workspace yet.
            if (app.dv.userWorkspace != null && app.dv.userWorkspace.userProfile) {
                app.dv.userWorkspace.userProfile.updateUserContext();
            }
            if (app.designer && app.designer.workspace && app.designer.workspace.userProfile) {
                app.designer.workspace.userProfile.updateUserContext();
            }

            // callback to calling routine.
            callback(result);
        };

        //We need to overwrite the config with the full config sent back with a designer response...EK
        if (r.EntityCache.Config != null) {
            r.EntityCache.Config.ConfigValue = JSON.parse(r.EntityCache.Config.ConfigValue);
            if (r.EntityCache.Config.ConfigValue.SavedThemes == null || r.EntityCache.Config.ConfigValue.SavedThemes.length === 0) {
                r.EntityCache.Config.ConfigValue.SavedThemes = s.configEntity.ConfigValue.SavedThemes;
            }
        }

        // we need to check the service response, because we have not loaded the designer objects yet.
        if (!r.IsDesigner) {
            if (app.designer != null) {
                // we need to remove designer-specific endpoints from the service before we unload the designer.
                app.designer.removeEndpoints();
                app.designer.deregisterDesignerEntities();

                // now "unload" the designer - prevent non-designer from accessing these objects/functionality.
                app.designer = null;
            }

            // if we are not a designer, we need to revert back to previous "public" config...WR
            if (r.EntityCache.Config != null) {
                s.configEntity = r.EntityCache.Config;
                s.config = s.configEntity.ConfigValue;
                s.appContext.config = s.config;

                // store public cache options.
                CA.loadStyleCache(r.EntityCache.Styles);

                CA.loadValueStatusCache(r.EntityCache.ValueStatuses);

                CA.loadExtensionCache(r.EntityCache.Extensions);
            }

            // Populate the extension
            if (flagSwitch) {
                // we do not want to persist and cause this switched user to be the default logged in user.
                s.getUserInfoValues(() => {
                    updateWorkspaces(true);
                }, keepLocalVariables);
            }
            else {
                s.finishLogin(rememberMeFlag, cacheCredentialsFlag, updateWorkspaces, SSOType, keepLocalVariables);
            }
        }
        else {
            if (app.designer != null) {
                loadDesignerCache(r, rememberMeFlag, cacheCredentialsFlag, SSOType, flagSwitch, updateWorkspaces, keepLocalVariables);
            }
            else {
                // load the designer
                return import(/* webpackChunkName: "designer" */ '../designer/designer').then((designer) => {
                    app.designer = designer.default;
                    if (app.dv.designerController == null) {
                        Object.defineProperty(app.dv, 'designerController', {
                            get() {
                                //if(process.env.NODE_ENV === 'development') {
                                //console.warn('DEPRECATED: Import designerController components instead of using app.dv.designerController');
                                //}
                                return designer.default;
                            }
                        });
                    }

                    loadDesignerCache(r, rememberMeFlag, cacheCredentialsFlag, SSOType, flagSwitch, updateWorkspaces, keepLocalVariables);
                });
            }
        }
    };

    s.authenticateUser = function (loginInfo, rememberMeFlag, cacheCredentialsFlag, callback, SSOType) {
        // Evaluate whether we are sending up a pass text, or hash signature to the service...AL
        var isPasswordHashed = false;
        if (app.dv.cache.session.appContext.user
            && app.dv.cache.session.appContext.user.Definition.clientProperties.stayLoggedIn == true
            && app.dv.cache.session.config.canCacheCredentials == true) {
            isPasswordHashed = true;
            cacheCredentialsFlag = true;
        }

        if(!loginInfo.UserLoginID) {
            throw new Error('authenticateUser called without a UserLoginID')
        } else if(loginInfo.Password) {
            loginInfo.IsHash = isPasswordHashed;
            loginInfo.isReauth = false;
        }

        loginInfo.SupportsES6 = api.utils.supportsES6();

        services.call(services.endpoints.authenticateUser, loginInfo, function (r, error) {
            if (r != null) {
                s.setUserContext(r, rememberMeFlag, cacheCredentialsFlag, SSOType, false, (success) => {
                    let authResult = {
                        isAuthenticated: success
                    };
                    callback(authResult);
                });
            } else {
                let authResult = {
                    isAuthenticated: false,
                    message: error.message || "Oops! The username and password combination is invalid."
                };
                callback(authResult);
            }
        });

        // TODO: Authenticate from local storage while offline?
    };

    /**
     * Switch the current user to the specified credentials. IF they fail, they should
     * still be under the current user context.
     * @param loginID
     * @param password
     * @return {Promise}
     */
    s.switchUser = function (loginID, password) {
        return new Promise((resolve, reject) => {
            let postData = {
                UserLoginID: loginID,
                Password: password,
                IsHash: false,
                IsReauth: false,
                SupportsES6: api.utils.supportsES6()
            };

            if (app.dv.cache.snapshot != null && app.dv.cache.snapshot.isActive){
                postData.skipEntityDownload = true;     // Set this to true so no configuration is pulled...EK
            }

            services.call(services.endpoints.switchUser, postData, (r, err) => {
                if (r != null) {
                    // clear out our current user context...
                    s.logUserOut(() => {
                        if (app.designer && app.designer.workspace && !r.IsDesigner) {
                            // We switched to a non-designer, do not show the designer rails if they existed
                            app.designer.workspace.hideDockers();

                            // "move" the user profile to the user top docker
                            app.dv.userWorkspace.topDocker.userProfile.setVisible(true);
                            app.designer.workspace.topDocker.userProfile.setVisible(false);
                        }
                        // ...and set the user context to the response values.
                        s.setUserContext(r, false, false, undefined, true, (success) => {
                            if (app.designer && app.designer.workspace && r.IsDesigner) {
                                // Refresh the pending changes in the top rail to show the new designer's pending changes
                                app.designer.workspace.topDocker.refreshPendingChanges();

                                // "move" the user profile to the designer top docker
                                app.dv.userWorkspace.topDocker.userProfile.setVisible(false);
                                app.designer.workspace.topDocker.userProfile.setVisible(true);

                                // Ensure the designer rails are shown
                                app.designer.workspace.showDockers();
                            }
                            resolve(success);
                        }, true)
                    }, false, true);
                }
                else {
                    // there was an error of some kind; let the caller know.
                    console.warn(err.message);
                    resolve(false);
                }
            });
        });
    };

    s.reauthorize = function (loginID, password, callback) {
        var postData = {
            UserLoginID: loginID,
            Password: password,
            IsHash: false,
            IsReauth: true
        };
        services.call(services.endpoints.authenticateUser, postData, (r, error) => {
            if (r == null || error != null) {
                callback(false);
            } else {
                callback(r);
            }
        });
    };

    s.resetUserPassword = function (emailAddress, projectName, callback) {
        var postData = {
            EmailAddress: emailAddress,
            ProjectName: projectName
        };
        services.call(services.endpoints.resetUserPassword, postData, callback);
    };

    /**
     * This function does other login logic in order for pending client changes to be applied before the startup view actually loads
     * @param {boolean} rememberMeFlag
     * @param {boolean} cacheCredentialsFlag
     * @param {function} callback
     * @param {string} SSOType
     * @param {boolean} [keepLocalVariables = false] - Flag to determine if we should keep the "dvVar" local variables
     */
    s.finishLogin = function(rememberMeFlag, cacheCredentialsFlag, callback, SSOType, keepLocalVariables = false){
        // Handle the case where the user wants to be remembered for subsequent logins.  If the user does not want to be remembered, ensure that we remove the user from teh app context (if it already exists).
        // Note the save to indexed db is done OOP - no need to wait for a callback here...AL
        if (rememberMeFlag == true) {
            s.appContext.user = s.currentUser.User;
            var appContextClientProps = app.dv.cache.session.appContext.user.Definition.clientProperties;
            if (cacheCredentialsFlag == true && (typeof appContextClientProps.expirationDate == "undefined" || appContextClientProps.expirationDate == null)) {
                if (app.dv.cache.session.config.passwordPolicySettings != null) {
                    var dateVal = new Date();
                    // For testing...TD
                    //dateVal.addMinutes(1);
                    dateVal.addDays(app.dv.cache.session.config.passwordPolicySettings.credentialCacheLength);
                    appContextClientProps.expirationDate = dateVal;
                }
            }
            else if (cacheCredentialsFlag == false || typeof appContextClientProps.expirationDate == "undefined") {
                appContextClientProps.expirationDate = null;
            }
            s.appContext.user.Definition.clientProperties.stayLoggedIn = cacheCredentialsFlag;
            s.appContext.user.Definition.clientProperties.stayLoggedInSSOType = SSOType;
            s.persistAppContext();
        } else if (rememberMeFlag == false && s.appContext.user) {
            s.appContext.user = null;
            s.persistAppContext();
        }

        s.getUserInfoValues(function () {
            callback(true);
        }, keepLocalVariables);
    };

    /**
     * Clears and repopulates the variables dictionary
     * @param {function} [callback = tools.noop] - Function that is called when the variables are done populating
     * @param {boolean} [keepLocalVariables = false] - Flag to determine if we should keep the "dvVar" local variables
     * @returns {Promise}
     */
    s.getUserInfoValues = async function (callback = tools.noop, keepLocalVariables = false) {

        // Remove any user variables
        s.clearVariables(keepLocalVariables);

        s.variables.add("{dvLoginID}", new app.dv.entities.user.infoVariable(s.currentUser.User.Definition.LoginID, new app.dv.entities.DynamicValue(s.currentUser.User.Definition.LoginID)));
        s.variables.add("{dvFirstName}", new app.dv.entities.user.infoVariable(s.currentUser.User.Definition.FirstName, new app.dv.entities.DynamicValue(s.currentUser.User.Definition.FirstName)));
        s.variables.add("{dvLastName}", new app.dv.entities.user.infoVariable(s.currentUser.User.Definition.LastName, new app.dv.entities.DynamicValue(s.currentUser.User.Definition.LastName)));

        let reqData = {
            Session: s.sessionGUID,
            userLoginID: s.currentUser.User.LoginID
        };

        let results;
        try {
            results = await services.call(services.endpoints.getUserInfoValues, reqData);
        } catch (e) {
            app.browser.notifier.showToast("There was an issue getting user information. Please contact your system administrator.", 6000);
            console.error(`There was an issue getting user information.  Error: ${(e && e.message) || e || "Unknown error"}`);
            callback();
        }

        if (results != null) {
            results.forEach((currVar) => {
                s.variables.add(currVar.FilterString, currVar);
            });
            callback(s.variables);
            return s.variables;
        }
        else {
            callback(); // No records -- TODO: error trap
        }
    };

    s.applyVariablesColumnFilter = function (toExpression, ColumnFilterOperator) {
        if (typeof toExpression === "string") {
            toExpression = toExpression.replace(/{(dv)[^{]*}/g, (varKey) => {
                let variableObject = s.variables.getVal(varKey);
                if (variableObject != null && variableObject.InfoValue != null && variableObject.InfoValue.Value != null) {
                    // We have a variable value, use it
                    let varVal = variableObject.InfoValue.Value;
                    switch (ColumnFilterOperator) {
                        case 'In':
                        case 'Not In':
                        case 'Special':
                            // single quote escape any variables injected into special filters
                            varVal = String(varVal).split("'").join("''");
                            break;
                        default:
                            break;
                    }
                    return varVal;
                }
                else {
                    // No variable was found or its value was null, replace it with nothing
                    return "";
                }
            });
        }

        return toExpression;
    };

    s.applyVariables = function (toExpression, singleQuoteReplacement) {
        if (toExpression != null && typeof toExpression == "string") {
            //Always replace the single quote replacement if needed...EK
            toExpression = toExpression.replace("{singlequote}", singleQuoteReplacement);

            var variableLines = toExpression.split("{");

            // each array element (after [0]) is the start of a variable name, so start at [1].
            for (var i = 1, il = variableLines.length; i < il; i++) {
                var varKey = "{" + variableLines[i].split("}")[0] + "}";

                // we now have the variable name, directly look it up.
                var varVal, variableObject = s.variables.getVal(varKey);

                // if the variable was not found, skip it.
                if (variableObject == null) { continue; }
                try {
                    varVal = variableObject.InfoValue.Value;
                }
                catch (e) {
                    varVal = null;
                }

                // handle null values.
                if (varVal == null) { varVal == ""; }

                toExpression = toExpression.split(varKey).join(varVal);
            }
        }

        return toExpression;
    };

    s.saveUsers = function (users, callback) {
        var postData = {
            Session: CA.session.sessionGUID,
            EntList: []
        }

        var currUser, cloneUser;

        for (var i = 0, l = users.length; i < l; i++) {
            currUser = users[i];

            // Toggle the state to modified if it's not new...AL
            if (currUser._State != app.dv.types.EntityStates.NewObject) {
                currUser._State = app.dv.types.EntityStates.Modified;
            } else {
                // NOTE: TODO: we are not creating new users at this point.  This will be revisited when we implement configuration...AL
            }

            cloneUser = tools.deepClone(currUser);
            cloneUser.Definition.LastPWUpdate = tools.toMSDateTime(cloneUser.Definition.LastPWUpdate, true);
            cloneUser.Definition.LastSuccessLogin = tools.toMSDateTime(cloneUser.Definition.LastSuccessLogin, true);
            cloneUser.Definition = JSON.stringify(cloneUser.Definition);

            postData.EntList.push(cloneUser);
        }

        services.call(services.endpoints.saveUsers, postData, function (r) {
            if (r.SaveChangesResult && !r.SaveChangesResult[0].ThrownException) {
                // TODO: When we implement configuration, we will need to do a merge here for new users,
                // right now, we are only modifying the current logged in user...AL
                s.currentUser.User._State = app.dv.types.EntityStates.Unmodified;

                callback(true);
            }
            else callback(false);
        });
    };

    /**
     * @param callback
     * @returns {Promise}
     */
    s.loadConfig = async function (callback = tools.noop) {
        let configRecord = null;
        if (app.dv.cache.snapshot.isActive && app.dv.cache.snapshot.config != null) {
            // Pull from the config snapshot...EK
            configRecord = app.dv.cache.snapshot.config;
        }
        else {
            try {
                configRecord = await services.call(services.endpoints.getConfig);
            }
            catch (e) {
                callback(e);
            }
        }

        if (configRecord != null) {
            if (typeof configRecord.ConfigValue === 'string') {
                configRecord.ConfigValue = tools.entityParse(configRecord.ConfigValue);
            }

            s.configEntity = configRecord;
            s.config = s.configEntity.ConfigValue;

            app.dv.envName = s.config.name;
            app.dv.appSpace = {};
            app.dv.appSpace.token = s.config.token;
            app.dv.appSpace.id = "a";
            app.dv.appSpace.version = s.config.version;
            localStorage.initializeDBs();

            await new Promise((resolve) => {
                //We have to load the app context in order to be able to cache credentials.  It has to be done after we initialize the DBs.
                //Within, we set the config and then persist the app context...BC
                s.loadAppContext(function () {
                    s.appContext.config = s.config;
                    s.persistAppContext();

                    resolve();
                    callback();
                });
            });
        }
    };

    s.startupView = function (callback) {
        if (s.config && s.config.OnlineStartupViewID != null) {
            //catch any errors related to the startup view configurations ...WR/MD
            try {
                app.dv.mvc.loadMVC(s.config.OnlineStartupViewID, s.config.showStartView === true , null, callback);
            } catch (e) {
                callback();
            }
        }
        else {
            callback();
        }
    };

    /**
     * Authenticates the guest user associated with the specified public form and returns the public form.
     * @param {String|Object} args - The form's API name OR a linker object
     * @returns {Promise<Object>} - An object containing the results of authenticateUser (r.user) and the ID of the public form (r.viewID)
     */
    s.getPublicForm = async function (args) {
        if (app.dv.cache.snapshot != null && app.dv.cache.snapshot.isActive) {
            args.skipEntityDownload = true;
        }

        args.SupportsES6 = api.utils.supportsES6();

        return services.call(services.endpoints.getPublicForm, args).then((res) => {
            return new Promise((resolve, reject) => {
                if (res != null) {
                    // we do not want to "remember" or cache these credentials, and this is not a "switch", as there was no original user context to change from.
                    s.setUserContext(res, false, false, null, false, () => {
                        // we only want the calling routine to have access to the specific view ID linker and open mode from this call.
                        resolve({
                            ViewID: res.ViewID,
                            openMode: res.openMode
                        });
                    });
                } else {
                    reject();
                }
            });
        });
    };

    //#endregion

    //#region local storage calls

    s.loadAppContext = function (callback) {
        localStorage.getRecord(localStorage.dataStores.AppContext, 0, function (r) {
            if (r != null) {
                s.appContext = r;
            }
            if (callback) callback(s.appContext);
        });
    };

    s.persistAppContext = function (callback) {
        // Make sure we always have the context id...AL
        s.appContext.ContextID = 0;
        // Update the app context in the local indexed db...AL
        localStorage.updateOrInsertRecord(localStorage.dataStores.AppContext, s.appContext, callback);
    };

    //#endregion

    s.updateLiveSelection = function (value, property, columnProperty, record, view, callback) {
        let historyBrowser = app.windows.FieldHistory.browser;

        s.liveSelection.Value = value;
        s.liveSelection.Property = property;
        s.liveSelection.ColumnProperty = columnProperty;
        s.liveSelection.Record = record;
        s.liveSelection.View = view;
        if (historyBrowser != null) {
            historyBrowser.updateWindow(historyBrowser, s.liveSelection, callback);
        }
    };

    s.liveSelection = {
        Value: null,
        Property: null,
        ColumnProperty: null,
        Record: null,
        View: null,
        RecordPropertyIDs: null
    };

    /**
     * Initiate and show the login component to allow changing password
     */
    s.changePassword = function () {
        /**
         * Get the login KO model component
         */
        let login = app.dv.modelLogin();

        login.show(
            app.dv.compLogin.types.changePassword,
            /**
             * Callback when the user has completed the login task
             * @param {boolean} isAuthenticated
             */
            (isAuthenticated) => {
                if (isAuthenticated) {
                    // change in password was successful
                    app.dv.actionLibrary.executeShowMessage(null, null, null, "Password has been changed successfully!", null, null, false, null);
                }
            });
    };

    return s;
});
